import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import Modal from './Modal';
import SearchSelect from './SearchSelect';
import { inr, num, day, apiError } from '../utils/format';

// Shared dialogs for the supplier module: the supplier record itself, a purchase
// bill (what we owe) and a payment (which settles one or more of those bills).
// Both Suppliers and SupplierDetail render these.

export const PAY_MODES = [['bank', 'Bank'], ['upi', 'UPI'], ['cash', 'Cash'], ['cheque', 'Cheque'], ['card', 'Card'], ['other', 'Other']];
export const BILL_STATUS = { open: 'badge-gold', partial: 'badge-blue', paid: 'badge-green', cancelled: 'badge-gray' };

const today = () => new Date().toISOString().slice(0, 10);
const n = (v) => Number(v) || 0;

// ── Supplier record ──────────────────────────────────────────────────────────
const emptySupplier = {
  name: '', contactName: '', phone: '', email: '', gstin: '', address: '', terms: '', isActive: true,
  bankName: '', bankAccount: '', ifsc: '', accountName: '', upiId: '',
};

export const SupplierModal = ({ supplier, onClose, onSaved }) => {
  const [form, setForm] = useState(supplier ? { ...emptySupplier, ...supplier } : emptySupplier);
  const [busy, setBusy] = useState(false); const [error, setError] = useState('');
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const save = async (e) => {
    e.preventDefault(); setBusy(true); setError('');
    try {
      const { data } = supplier ? await api.put(`/suppliers/${supplier.id}`, form) : await api.post('/suppliers', form);
      toast.success('Saved'); onSaved(data.supplier); onClose();
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <Modal title={supplier ? 'Edit supplier' : 'Add supplier'} onClose={onClose}
      footer={<><button className="btn btn-outline" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button></>}>
      <form onSubmit={save}>
        <div className="row">
          <div className="field" style={{ flex: 2 }}><label>Supplier name *</label><input value={form.name} onChange={(e) => set('name', e.target.value)} required /></div>
          <div className="field"><label>Contact person</label><input value={form.contactName || ''} onChange={(e) => set('contactName', e.target.value)} /></div>
        </div>
        <div className="row">
          <div className="field"><label>Phone</label><input value={form.phone || ''} onChange={(e) => set('phone', e.target.value)} /></div>
          <div className="field"><label>Email</label><input type="email" value={form.email || ''} onChange={(e) => set('email', e.target.value)} /></div>
          <div className="field"><label>GSTIN</label><input value={form.gstin || ''} onChange={(e) => set('gstin', e.target.value)} /></div>
        </div>
        <div className="field"><label>Address</label><textarea rows={2} value={form.address || ''} onChange={(e) => set('address', e.target.value)} /></div>
        <div className="field"><label>Payment terms</label><input value={form.terms || ''} onChange={(e) => set('terms', e.target.value)} placeholder="e.g. Net 30" /></div>

        {/* Where their money goes. A bank transfer needs the account number and
            IFSC together; the name has to match the account, which is not always
            the name the supplier trades under. */}
        <div className="section-head"><span className="muted small">Bank details — for paying them</span></div>
        <div className="row">
          <div className="field" style={{ flex: 2 }}><label>Account holder name <span className="muted small">(as per bank)</span></label>
            <input value={form.accountName || ''} onChange={(e) => set('accountName', e.target.value)} placeholder={form.name || 'Leave blank to use the supplier name'} /></div>
          <div className="field"><label>Bank</label><input value={form.bankName || ''} onChange={(e) => set('bankName', e.target.value)} placeholder="e.g. HDFC Bank" /></div>
        </div>
        <div className="row">
          <div className="field"><label>Account number</label><input value={form.bankAccount || ''} onChange={(e) => set('bankAccount', e.target.value)} /></div>
          <div className="field"><label>IFSC</label><input value={form.ifsc || ''} onChange={(e) => set('ifsc', (e.target.value || '').toUpperCase())} placeholder="HDFC0000053" /></div>
          <div className="field"><label>UPI ID <span className="muted small">(if they take UPI)</span></label>
            <input value={form.upiId || ''} onChange={(e) => set('upiId', e.target.value)} placeholder="name@bank" /></div>
        </div>
        <label className="flex gap" style={{ alignItems: 'center' }}><input type="checkbox" style={{ width: 'auto' }} checked={form.isActive} onChange={(e) => set('isActive', e.target.checked)} /> Active</label>
        {error && <p className="form-error">{error}</p>}
      </form>
    </Modal>
  );
};

// Raw materials + bought products as one pickable catalog for bill lines. Both
// lists are optional — a user without products/rawmaterials read access can still
// type a description, so a failed fetch just means fewer suggestions.
export const useCatalog = () => {
  const [options, setOptions] = useState([]);
  useEffect(() => {
    Promise.all([
      api.get('/raw-materials').then(({ data }) => data.materials || []).catch(() => []),
      api.get('/products').then(({ data }) => data.products || []).catch(() => []),
    ]).then(([mats, prods]) => setOptions([
      ...mats.map((m) => ({
        value: `raw:${m.id}`, label: m.name, sub: `Raw material · ${m.unit || 'kg'}`,
        search: `${m.name} ${m.sku || ''} ${m.itemCode || ''}`,
        itemType: 'raw', itemId: m.id, unit: m.unit || 'kg', rate: n(m.purchasePrice),
      })),
      ...prods.map((p) => ({
        value: `product:${p.id}`, label: p.name, sub: `Product · ${p.unit || 'unit'}`,
        search: `${p.name} ${p.id} ${p.code || ''}`,
        itemType: 'product', itemId: p.id, unit: p.unit || 'unit', rate: n(p.purchasePrice),
      })),
    ]));
  }, []);
  return options;
};

// ── Purchase bill ────────────────────────────────────────────────────────────
const emptyLine = () => ({ pick: '', itemType: 'other', itemId: 0, description: '', qty: '', unit: 'kg', rate: '', gstRate: '' });

export const BillModal = ({ bill, suppliers, presetSupplierId, onClose, onSaved }) => {
  const editing = !!bill;
  const [form, setForm] = useState({
    supplierId: bill?.supplierId || presetSupplierId || '',
    billNumber: bill?.billNumber || '',
    billDate: bill?.billDate || today(),
    dueDate: bill?.dueDate || '',
    otherCharges: bill ? String(bill.otherCharges || '') : '',
    note: bill?.note || '',
  });
  const [lines, setLines] = useState(() => (bill?.items?.length
    ? bill.items.map((i) => ({
      pick: i.itemId ? `${i.itemType}:${i.itemId}` : '', itemType: i.itemType, itemId: i.itemId,
      description: i.description || '', qty: String(i.qty), unit: i.unit, rate: String(i.rate), gstRate: String(i.gstRate || ''),
    }))
    : [emptyLine()]));
  const [orders, setOrders] = useState([]);
  const [busy, setBusy] = useState(false); const [error, setError] = useState('');
  const catalog = useCatalog();

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setLine = (i, patch) => setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  // Vendor POs raised on this supplier — a bill usually arrives against one.
  useEffect(() => {
    if (!form.supplierId || editing) { setOrders([]); return; }
    api.get('/procurement?target=vendor').then(({ data }) => setOrders((data.orders || []).filter((o) => Number(o.vendorId) === Number(form.supplierId))))
      .catch(() => setOrders([]));
  }, [form.supplierId, editing]);

  const loadFromOrder = async (orderId) => {
    if (!orderId) return;
    try {
      const { data } = await api.get(`/suppliers/bills/from-order/${orderId}`);
      if (!data.items?.length) return toast.error('That order has no billable lines');
      setLines(data.items.map((i) => ({
        pick: `${i.itemType}:${i.itemId}`, itemType: i.itemType, itemId: i.itemId,
        description: i.description, qty: String(i.qty), unit: i.unit, rate: String(i.rate), gstRate: '',
      })));
      if (data.poNumber && !form.billNumber) set('billNumber', '');
      toast.success(`Loaded ${data.items.length} line(s) from ${data.code}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const amountOf = (l) => Math.round(n(l.qty) * n(l.rate) * 100) / 100;
  const taxOf = (l) => Math.round(amountOf(l) * (n(l.gstRate) / 100) * 100) / 100;
  const subtotal = lines.reduce((s, l) => s + amountOf(l), 0);
  const tax = lines.reduce((s, l) => s + taxOf(l), 0);
  const total = Math.round((subtotal + tax + n(form.otherCharges)) * 100) / 100;
  const usable = lines.filter((l) => (l.description || l.itemId) && n(l.qty) > 0 && n(l.rate) > 0);
  const canSave = form.supplierId && form.billDate && usable.length > 0 && total > 0;

  const save = async () => {
    setBusy(true); setError('');
    try {
      const payload = {
        ...form,
        supplierId: Number(form.supplierId),
        dueDate: form.dueDate || undefined,
        otherCharges: n(form.otherCharges),
        items: usable.map((l) => ({
          itemType: l.itemType, itemId: l.itemId, description: l.description,
          qty: n(l.qty), unit: l.unit, rate: n(l.rate), gstRate: n(l.gstRate),
        })),
      };
      const { data } = editing ? await api.put(`/suppliers/bills/${bill.id}`, payload) : await api.post('/suppliers/bills', payload);
      toast.success(editing ? 'Bill updated' : `Bill ${data.bill.code} recorded`);
      onSaved(data.bill); onClose();
    } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Modal wide title={editing ? `Edit bill ${bill.code}` : 'New supplier bill'} onClose={onClose}
      footer={<><button className="btn btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={busy || !canSave}>{busy ? 'Saving…' : `Save · ${inr(total)}`}</button></>}>
      <div className="row">
        <div className="field" style={{ flex: 2 }}><label>Supplier *</label>
          <select value={form.supplierId} onChange={(e) => set('supplierId', e.target.value)} disabled={editing}>
            <option value="">— select —</option>
            {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div className="field"><label>Their bill no.</label><input value={form.billNumber} onChange={(e) => set('billNumber', e.target.value)} placeholder="Invoice number" /></div>
        <div className="field"><label>Bill date *</label><input type="date" value={form.billDate} onChange={(e) => set('billDate', e.target.value)} /></div>
        <div className="field"><label>Due date</label><input type="date" value={form.dueDate} onChange={(e) => set('dueDate', e.target.value)} /></div>
      </div>

      {!editing && orders.length > 0 && (
        <div className="field" style={{ maxWidth: 420 }}><label>Bill against a purchase order (optional)</label>
          <select defaultValue="" onChange={(e) => loadFromOrder(e.target.value)}>
            <option value="">— start from scratch —</option>
            {orders.map((o) => <option key={o.id} value={o.id}>{o.code}{o.poNumber ? ` · PO ${o.poNumber}` : ''} · {o.status}</option>)}
          </select>
        </div>
      )}

      <div className="section-head" style={{ marginTop: '.4rem' }}>
        <h3 style={{ fontSize: '.95rem', margin: 0 }}>Lines</h3>
        <button className="btn btn-outline btn-sm" onClick={() => setLines((p) => [...p, emptyLine()])}>+ Add line</button>
      </div>
      {/* overflow:visible so the SearchSelect dropdown isn't clipped */}
      <div className="table-wrap" style={{ overflow: 'visible' }}>
        <table className="grid">
          <thead><tr><th style={{ minWidth: 190 }}>Item</th><th>Description</th><th className="right">Qty</th><th>Unit</th><th className="right">Rate</th><th className="right">GST %</th><th className="right">Amount</th><th></th></tr></thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i}>
                <td>
                  <SearchSelect placeholder="Search material or product…" value={l.pick}
                    options={catalog.map((o) => ({ value: o.value, label: o.label, sub: o.sub, search: o.search }))}
                    onChange={(v) => {
                      const o = catalog.find((x) => x.value === v);
                      setLine(i, o
                        ? { pick: v, itemType: o.itemType, itemId: o.itemId, description: o.label, unit: o.unit, rate: l.rate || (o.rate ? String(o.rate) : '') }
                        : { pick: '', itemType: 'other', itemId: 0 });
                    }} />
                </td>
                <td><input value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} placeholder="Freight, packing…" /></td>
                <td><input type="number" min="0" step="0.001" style={{ width: 90 }} value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} /></td>
                <td><input style={{ width: 70 }} value={l.unit} onChange={(e) => setLine(i, { unit: e.target.value })} /></td>
                <td><input type="number" min="0" step="0.01" style={{ width: 100 }} value={l.rate} onChange={(e) => setLine(i, { rate: e.target.value })} /></td>
                <td><input type="number" min="0" step="0.01" style={{ width: 70 }} value={l.gstRate} onChange={(e) => setLine(i, { gstRate: e.target.value })} placeholder="0" /></td>
                <td className="right">{inr(amountOf(l) + taxOf(l))}</td>
                <td><button className="btn btn-danger btn-sm" onClick={() => setLines((p) => (p.length === 1 ? [emptyLine()] : p.filter((_, idx) => idx !== i)))}>✕</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="row" style={{ marginTop: '.6rem', alignItems: 'flex-end' }}>
        <div className="field" style={{ maxWidth: 160 }}><label>Other charges (₹)</label><input type="number" step="0.01" value={form.otherCharges} onChange={(e) => set('otherCharges', e.target.value)} placeholder="freight / round-off" /></div>
        <div className="field" style={{ flex: 1 }}><label>Note</label><input value={form.note} onChange={(e) => set('note', e.target.value)} /></div>
        <div className="field" style={{ maxWidth: 240 }}>
          <div className="muted small">Subtotal {inr(subtotal)} · GST {inr(tax)}</div>
          <div style={{ fontSize: '1.15rem', fontWeight: 700 }}>Total {inr(total)}</div>
        </div>
      </div>
      {error && <p className="form-error">{error}</p>}
    </Modal>
  );
};

// ── One bill, with its lines and the payments against it ─────────────────────
export const BillView = ({ billId, canWrite, onClose, onChanged, onEdit }) => {
  const [bill, setBill] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/suppliers/bills/${billId}`).then(({ data }) => setBill(data.bill)).catch((e) => { toast.error(apiError(e)); onClose(); });
  }, [billId]);

  const act = async (what) => {
    const label = what === 'delete' ? 'Delete' : 'Cancel';
    if (!window.confirm(`${label} bill ${bill.code}?`)) return;
    setBusy(true);
    try {
      if (what === 'delete') await api.delete(`/suppliers/bills/${bill.id}`);
      else await api.post(`/suppliers/bills/${bill.id}/cancel`);
      toast.success(`Bill ${what === 'delete' ? 'deleted' : 'cancelled'}`);
      onChanged(); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (!bill) return <Modal title="Bill" onClose={onClose}><div className="spinner" /></Modal>;
  const unpaid = Number(bill.paidAmount) < 0.005;
  return (
    <Modal wide title={`${bill.code}${bill.billNumber ? ` · ${bill.billNumber}` : ''}`} onClose={onClose}
      footer={<>
        <button className="btn btn-outline" onClick={onClose}>Close</button>
        {canWrite && unpaid && bill.status !== 'cancelled' && <button className="btn btn-outline" onClick={() => { onEdit(bill); onClose(); }}>Edit</button>}
        {canWrite && unpaid && bill.status !== 'cancelled' && <button className="btn btn-outline" onClick={() => act('cancel')} disabled={busy}>Cancel bill</button>}
        {canWrite && unpaid && <button className="btn btn-danger" onClick={() => act('delete')} disabled={busy}>Delete</button>}
      </>}>
      <div className="flex gap" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div>
          <div><strong>{bill.supplier?.name}</strong>{bill.supplier?.gstin ? <span className="muted small"> · {bill.supplier.gstin}</span> : null}</div>
          <div className="muted small">Billed {day(bill.billDate)}{bill.dueDate ? ` · due ${day(bill.dueDate)}` : ''}</div>
        </div>
        <div className="right">
          <div style={{ fontSize: '1.2rem', fontWeight: 700 }}>{inr(bill.total)}</div>
          <div className="muted small">paid {inr(bill.paidAmount)} · balance {inr(bill.balance)}</div>
        </div>
      </div>

      <div className="table-wrap" style={{ marginTop: '.6rem' }}>
        <table className="grid">
          <thead><tr><th>Item</th><th className="right">Qty</th><th className="right">Rate</th><th className="right">GST</th><th className="right">Total</th></tr></thead>
          <tbody>
            {(bill.items || []).map((i) => (
              <tr key={i.id}>
                <td>{i.description || `#${i.itemId}`}<span className="muted small"> · {i.itemType}</span></td>
                <td className="right">{num(i.qty)} {i.unit}</td>
                <td className="right">{inr(i.rate)}</td>
                <td className="right">{Number(i.gstRate) ? `${Number(i.gstRate)}% · ${inr(i.taxAmount)}` : '—'}</td>
                <td className="right">{inr(i.total)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="total-row"><td colSpan={4}>Subtotal + GST{Number(bill.otherCharges) ? ` + other ${inr(bill.otherCharges)}` : ''}</td><td className="right"><strong>{inr(bill.total)}</strong></td></tr>
          </tfoot>
        </table>
      </div>

      <h3 style={{ fontSize: '.95rem', marginBottom: '.3rem' }}>Payments against this bill</h3>
      {(bill.allocations || []).length ? (
        <div className="table-wrap">
          <table className="grid">
            <thead><tr><th>Payment</th><th>Date</th><th>Mode</th><th>Reference</th><th className="right">Applied</th></tr></thead>
            <tbody>
              {bill.allocations.map((a) => (
                <tr key={a.id}>
                  <td>{a.payment?.code}</td>
                  <td>{day(a.payment?.paymentDate)}</td>
                  <td>{(a.payment?.mode || '').toUpperCase()}</td>
                  <td className="muted small">{a.payment?.reference || '—'}</td>
                  <td className="right">{inr(a.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <p className="muted small">Nothing paid yet.</p>}
      {bill.note && <p className="muted small">Note: {bill.note}</p>}
    </Modal>
  );
};

// ── Payment, allocated across that supplier's open bills ─────────────────────
export const PaymentModal = ({ suppliers, presetSupplierId, onClose, onSaved }) => {
  const [form, setForm] = useState({ supplierId: presetSupplierId || '', paymentDate: today(), amount: '', mode: 'bank', reference: '', note: '' });
  const [bills, setBills] = useState([]);
  const [alloc, setAlloc] = useState({});         // billId -> amount string
  const [busy, setBusy] = useState(false); const [error, setError] = useState('');
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    setAlloc({});
    if (!form.supplierId) { setBills([]); return; }
    api.get(`/suppliers/bills?supplierId=${form.supplierId}&status=due`)
      .then(({ data }) => setBills((data.bills || []).filter((b) => b.balance > 0.005).sort((a, b) => new Date(a.billDate) - new Date(b.billDate))))
      .catch(() => setBills([]));
  }, [form.supplierId]);

  const amount = n(form.amount);
  const allocated = useMemo(() => Math.round(Object.values(alloc).reduce((s, v) => s + n(v), 0) * 100) / 100, [alloc]);
  const leftover = Math.round((amount - allocated) * 100) / 100;
  const overAllocated = allocated > amount + 0.005;

  // Oldest bill first, until the money runs out — how a lump-sum transfer is
  // actually applied against a running account.
  const autoAllocate = () => {
    let left = amount;
    const next = {};
    for (const b of bills) {
      if (left <= 0.005) break;
      const take = Math.min(left, b.balance);
      next[b.id] = String(Math.round(take * 100) / 100);
      left = Math.round((left - take) * 100) / 100;
    }
    setAlloc(next);
  };

  const save = async () => {
    setBusy(true); setError('');
    try {
      const { data } = await api.post('/suppliers/payments', {
        ...form, supplierId: Number(form.supplierId), amount,
        allocations: Object.entries(alloc).map(([billId, v]) => ({ billId: Number(billId), amount: n(v) })).filter((a) => a.amount > 0),
      });
      toast.success(`Payment ${data.payment.code} recorded`);
      onSaved(data.payment); onClose();
    } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Modal wide title="Pay a supplier" onClose={onClose}
      footer={<><button className="btn btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={busy || !form.supplierId || !(amount > 0) || overAllocated}>{busy ? 'Saving…' : `Record · ${inr(amount)}`}</button></>}>
      <div className="row">
        <div className="field" style={{ flex: 2 }}><label>Supplier *</label>
          <select value={form.supplierId} onChange={(e) => set('supplierId', e.target.value)}>
            <option value="">— select —</option>
            {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}{s.outstanding > 0 ? ` — ${inr(s.outstanding)} due` : ''}</option>)}
          </select>
        </div>
        <div className="field"><label>Date *</label><input type="date" value={form.paymentDate} onChange={(e) => set('paymentDate', e.target.value)} /></div>
        <div className="field"><label>Amount (₹) *</label><input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => set('amount', e.target.value)} /></div>
      </div>
      <div className="flex gap" style={{ margin: '.2rem 0 .6rem' }}>
        {PAY_MODES.map(([m, label]) => (
          <button key={m} className={`pill-btn ${form.mode === m ? 'active' : ''}`} onClick={() => set('mode', m)}>{label}</button>
        ))}
      </div>
      <div className="row">
        <div className="field"><label>Reference {form.mode === 'cheque' ? '(cheque no.)' : '(UTR / txn id)'}</label><input value={form.reference} onChange={(e) => set('reference', e.target.value)} /></div>
        <div className="field" style={{ flex: 2 }}><label>Note</label><input value={form.note} onChange={(e) => set('note', e.target.value)} /></div>
      </div>

      <div className="section-head" style={{ marginTop: '.4rem' }}>
        <h3 style={{ fontSize: '.95rem', margin: 0 }}>Apply to bills</h3>
        <button className="btn btn-outline btn-sm" onClick={autoAllocate} disabled={!bills.length || !(amount > 0)}>Auto-allocate oldest first</button>
      </div>
      {form.supplierId && !bills.length && <p className="muted small">No open bills — the whole payment will be held as an advance.</p>}
      {bills.length > 0 && (
        <div className="table-wrap">
          <table className="grid">
            <thead><tr><th>Bill</th><th>Date</th><th className="right">Total</th><th className="right">Balance</th><th className="right">Apply</th></tr></thead>
            <tbody>
              {bills.map((b) => (
                <tr key={b.id}>
                  <td>{b.code}{b.billNumber ? <span className="muted small"> · {b.billNumber}</span> : null}</td>
                  <td className="muted small">{day(b.billDate)}</td>
                  <td className="right">{inr(b.total)}</td>
                  <td className="right">{inr(b.balance)}</td>
                  <td className="right">
                    <input type="number" min="0" step="0.01" style={{ width: 110 }} value={alloc[b.id] || ''}
                      onChange={(e) => setAlloc((p) => ({ ...p, [b.id]: e.target.value }))}
                      max={b.balance} placeholder="0" />
                    {n(alloc[b.id]) > b.balance + 0.005 && <div className="neg" style={{ fontSize: '.75rem' }}>over balance</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className={overAllocated ? 'form-error' : 'muted small'}>
        Allocated {inr(allocated)} of {inr(amount)}
        {overAllocated ? ' — more than the payment' : leftover > 0.005 ? ` · ${inr(leftover)} kept as an advance` : ''}
      </p>
      {error && <p className="form-error">{error}</p>}
    </Modal>
  );
};
